import { useState, useEffect } from 'react';
import { Pencil, Trash2, Check, X } from 'lucide-react';
import { Button } from './Button';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';
import { getMessageTypes, createMessageType, updateMessageType, deleteMessageType } from '../services/api';

export const MessageTypesManager = () => {
  const [messageTypes, setMessageTypes] = useState([]);
  const [newType, setNewType] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { t, language } = useLanguage();

  useEffect(() => {
    loadMessageTypes();
  }, []);

  const loadMessageTypes = async () => {
    try {
      setLoading(true);
      const types = await getMessageTypes();
      setMessageTypes(types);
    } catch (error) {
      console.error('Failed to load message types:', error);
      toast.error(t('failedToLoadMessageTypes') || 'Failed to load message types');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newType.trim()) {
      toast.error(t('messageTypeRequired') || 'Message type name is required');
      return;
    }

    try {
      await createMessageType({ name: newType.trim() });
      setNewType('');
      toast.success(t('messageTypeAdded') || 'Message type added successfully');
      await loadMessageTypes();
    } catch (error) {
      console.error('Failed to create message type:', error);
      toast.error(error.response?.data?.error?.message || t('failedToCreateMessageType') || 'Failed to create message type');
    }
  };

  const handleEdit = (id) => {
    const type = messageTypes.find(t => t.id === id);
    if (type) {
      setEditingId(id);
      setEditingValue(type.name);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingValue.trim()) {
      toast.error(t('messageTypeRequired') || 'Message type name is required');
      return;
    }

    try {
      await updateMessageType(editingId, { name: editingValue.trim() });
      setEditingId(null);
      setEditingValue('');
      toast.success(t('messageTypeUpdated') || 'Message type updated successfully');
      await loadMessageTypes();
    } catch (error) {
      console.error('Failed to update message type:', error);
      toast.error(error.response?.data?.error?.message || t('failedToUpdateMessageType') || 'Failed to update message type');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingValue('');
  };

  const handleDelete = async (id) => {
    if (!confirm(t('deleteMessageTypeConfirm') || 'Are you sure you want to delete this message type?')) {
      return;
    }

    try {
      await deleteMessageType(id);
      toast.success(t('messageTypeDeleted') || 'Message type deleted successfully');
      await loadMessageTypes();
    } catch (error) {
      console.error('Failed to delete message type:', error);
      toast.error(error.response?.data?.error?.message || t('failedToDeleteMessageType') || 'Failed to delete message type');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {t('messageTypes') || 'Message Types'}
        </h2>
        <p className="text-gray-600">
          {t('manageMessageTypes') || 'Define the types of messages that leads can receive'}
        </p>
      </div>

      {/* Add New Message Type */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex gap-3">
          <input
            type="text"
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
            placeholder={t('addNewMessageType') || 'Add new message type...'}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <Button onClick={handleAdd}>
            {t('add') || 'Add'}
          </Button>
        </div>
      </div>

      {/* Message Types List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {messageTypes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {t('noMessageTypes') || 'No message types defined yet'}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  {t('messageType') || 'Message Type'}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  {t('actions') || 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {messageTypes.map((type) => (
                <tr key={type.id}>
                  <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    {editingId === type.id ? (
                      <input
                        type="text"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit()}
                        className="w-full px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        autoFocus
                      />
                    ) : (
                      <span className="text-sm text-gray-900">{type.name}</span>
                    )}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    {editingId === type.id ? (
                      <div className={`flex items-center gap-3 ${language === 'he' ? 'justify-end' : 'justify-start'}`}>
                        <button
                          onClick={handleSaveEdit}
                          className="text-green-600 hover:text-green-900"
                          title={t('save') || 'Save'}
                        >
                          <Check className="w-5 h-5" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-gray-600 hover:text-gray-900"
                          title={t('cancel') || 'Cancel'}
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <div className={`flex items-center gap-3 ${language === 'he' ? 'justify-end' : 'justify-start'}`}>
                        <button
                          onClick={() => handleEdit(type.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title={t('edit') || 'Edit'}
                        >
                          <Pencil className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(type.id)}
                          className="text-red-600 hover:text-red-900"
                          title={t('delete') || 'Delete'}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
